import './GopparCalculator.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../../icons/ArrowRight';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import ToolFixedMenu from './ToolFixedMenu';


function GopparCalculator() {
    const langPrefix = useLanguagePrefix();
    const [operatingProfit, setOperatingProfit] = useState(null);
    const [roomAvailable, setRoomAvailable] = useState(null);
    const [GOPPAR, setGOPPAR] = useState(0);
    const { t } = useTranslation();


    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'profit') {
            setOperatingProfit(value);
        } else if (type === 'available') {
            setRoomAvailable(value);
        }
    };


    const calculateGOPPAR = () => {
        if (operatingProfit > 0 && roomAvailable > 0) {
            const result = operatingProfit / roomAvailable;
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setGOPPAR(formatted);
        } else {
            setGOPPAR(null);
        }
    };

    return (
        <div>
            <Helmet> 

                <title>GOPPAR Calculator – Gross Operating Profit Per Room Tool - Prostay</title>
                <meta name="description" content="Calculate your hotel’s GOPPAR with Prostay’s free tool. Understand your property’s profit per available room and gain insights into operational efficiency and performance." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/goppar-calculator/"} />

                 <link rel="alternate" href="https://www.prostay.com/es/goppar-calculator/" />
                 <link rel="alternate" href="https://www.prostay.com/en/goppar-calculator/" />

                <meta property="og:title" content="Occupancy Rate Calculator – Hotel Room Occupancy Formula - Prostay" />
                <meta property="og:description" content="Calculate your hotel’s GOPPAR with Prostay’s free tool. Understand your property’s profit per available room and gain insights into operational efficiency and performance." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/goppar-calculator/"}  />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Occupancy Rate Calculator – Hotel Room Occupancy Formula - Prostay" />
                <meta name="twitter:description" content="Calculate your hotel’s GOPPAR with Prostay’s free tool. Understand your property’s profit per available room and gain insights into operational efficiency and performance." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />


            </Helmet>
            <div className='d-flex goppar-calculator flex-column-reverse flex-xl-row'>
                <div className='d-flex flex-column width-left gap-4'>
                    <div>
                        <h1 style={{ margin: '0px' }}>
                            {t('GOPPAR Calculator - Gross Operating Profit Per Available Room')}
                        </h1>
                        <h6 className='mt-4'>
                            {t('Measure Your Hotel’s Profitability Per Room.')} <br />
                            {t('Use this free GOPPAR calculator to understand how much actual profit your hotel earns from every available room — not just revenue.')}
                        </h6>
                        <div className='d-flex flex-column-reverse flex-xl-row gap-2 gap-sm-5 mt-5'>
                            <div className='d-flex flex-column gap-4'>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Gross Operating Profit (GOP)')}
                                    </label>
                                    <input
                                        type='number'
                                        value={operatingProfit}
                                        onChange={(e) => handleInputChange(e, 'profit')}
                                        placeholder={t('Enter amount here')} />
                                </div>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Rooms Available')}
                                    </label>
                                    <input
                                        type='number'
                                        value={roomAvailable}
                                        onChange={(e) => handleInputChange(e, 'available')}
                                        placeholder={t('Enter number here')} />
                                </div>
                                <div className='d-flex gap-3 gap-sm-5 flex-column flex-sm-row align-items-center'>
                                    <button className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '111px' }} onClick={calculateGOPPAR}>
                                        {t('Calculate')}
                                        <ArrowRight />
                                    </button>
                                    <div className='d-flex flex-column w-100'>
                                        <label>
                                            {t('GOPPAR')}
                                        </label>
                                        <input
                                            readOnly
                                            value={GOPPAR}
                                            type='number'
                                            placeholder='0' />
                                    </div>
                                </div>
                            </div>
                            <div className='center-div'>
                                <h2>
                                    {t('What is GOPPAR?')}
                                </h2>
                                <p className='mt-3'>
                                    {t('GOPPAR, which stands for Gross Operating Profit Per Available Room, is a financial performance metric used in hospitality to measure how efficiently a hotel turns revenue into profit across all available rooms.')}
                                </p>
                                <br />
                                <p>
                                    {t('Unlike RevPAR, which only considers room revenue, GOPPAR includes all revenue sources — such as food and beverage, spa, and other services — and subtracts operating expenses. This makes it one of the most comprehensive profitability metrics available for hoteliers, providing insight into both top-line performance and cost control.')}
                                </p>
                                <br />
                                <p>
                                    {t('By tracking GOPPAR regularly, hotel managers and owners can better assess operational efficiency, compare properties, and make smarter business decisions that improve overall profitability.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='gap-3 d-flex flex-column mt-3'>
                        <h2>
                            {t('GOPPAR Formula')}
                        </h2>
                        <p>
                            {t('The standard formula for calculating GOPPAR is:')}
                        </p>
                        <div className='d-flex align-items-center formula gap-2'>
                            <p>
                                {t('GOPPAR')}  =
                            </p>
                            <div className='d-flex flex-column align-items-center'>
                                <p>
                                    {t('Gross Operating Profit')}
                                </p>
                                <p style={{ borderTop: '0.5px solid #1643E0' }}>
                                    {t('Total Available Rooms')}
                                </p>
                            </div>
                        </div>
                        <p>
                            {t('To apply this, you first determine your gross operating profit — which is your total hotel revenue minus all operating expenses (excluding interest, taxes, depreciation, and amortization). Then divide that profit by the number of rooms available over the period being analyzed.')}
                        </p>
                        <p>
                            {t('For example, if your hotel has 100 rooms and you’re analyzing a 30-day month, you would divide by 3,000 available room nights. The result shows how much profit your property is generating per room, regardless of whether the room was sold.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3'>
                        <h2>
                            {t('Why GOPPAR Matters in Hotel Management')}
                        </h2>
                        <p>
                            {t('GOPPAR is critical because it doesn’t just measure how much revenue a hotel earns — it shows how much profit is actually being retained after covering operating costs. This makes GOPPAR especially important for owners, investors, and finance teams who need a clear picture of a property’s financial health.')}
                        </p>
                        <p>
                            {t('While RevPAR and ADR tell you how much money is coming in from room sales, they ignore expenses. GOPPAR brings cost management into the equation, helping identify whether increased revenue is truly translating into bottom-line growth. It also helps spotlight inefficiencies in operations that might be eating away at profitability, even during high-occupancy periods.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('GOPPAR vs RevPAR and TRevPAR')}
                        </h2>
                        <p>
                            {t('While RevPAR and TRevPAR focus on revenue, GOPPAR focuses on profitability. RevPAR looks at room revenue per available room, and TRevPAR expands that to include total property revenue. However, neither of them considers expenses. That’s where GOPPAR sets itself apart. It tells you what’s left after the bills are paid.')}
                        </p>
                        <p>
                            {t('Two hotels might have similar RevPAR, but if one operates more efficiently, its GOPPAR will be higher — and that makes a real difference to owners and stakeholders. GOPPAR is especially useful when comparing hotels with different business models or departmental revenue structures.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('Make Profit-Driven Decisions with Prostay')}
                        </h2>
                        <p>
                            {t('Prostay doesn’t just help you track revenue — it helps you optimize for profit. With built-in financial tools like GOPPAR tracking, cost breakdowns, and real-time reporting, your hotel can easily identify where money is made — and where it’s being lost.')}
                        </p>
                        <p>
                            {t('Whether you’re managing a luxury resort, a lean boutique hotel, or a portfolio of properties, Prostay gives you the intelligence you need to make profitability-focused decisions that scale.')}
                        </p>
                    </div>
                </div>
               <ToolFixedMenu/>
            </div>
        </div>
    )
}

export default GopparCalculator;